/* Keep the layered menu isolated from Dawn's dropdown and mobile drawer behavior. */
if (!customElements.get('cs-category-menu')) {
  customElements.define('cs-category-menu', class extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.overlay = this.querySelector('.cs-category-overlay');
      this.header = this.closest('.section-header');
      this.triggers = [...this.querySelectorAll('.cs-category-trigger')];
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.details.addEventListener('toggle', () => this.syncOpen(), options);
      this.querySelectorAll('[data-menu-close]').forEach(button => {
        button.addEventListener('click', () => this.close(), options);
      });
      this.triggers.forEach(button => {
        button.addEventListener('click', () => this.selectBranch(button, button.getAttribute('aria-expanded') !== 'true'), options);
        button.addEventListener('keydown', event => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.selectBranch(button, true);
            this.querySelector(`#${button.getAttribute('aria-controls')} a`)?.focus();
          }
        }, options);
      });
      this.overlay.addEventListener('keydown', event => this.onKeyDown(event), options);
      // Dawn also handles Escape on keyup; prevent it from closing the parent twice.
      this.overlay.addEventListener('keyup', event => {
        if (event.key === 'Escape') event.stopPropagation();
      }, options);
      this.onResize = () => {
        if (window.innerWidth < 990) this.close(false);
        else this.updatePosition();
      };
      window.addEventListener('resize', this.onResize, options);
      document.addEventListener('pointerdown', event => {
        if (this.details.open && !this.contains(event.target)) this.close(false);
      }, options);
      document.addEventListener('focusin', event => {
        if (this.details.open && !this.contains(event.target)) this.close(false);
      }, options);
      window.addEventListener('pageshow', () => this.syncOpen(), options);
      this.resizeObserver = new ResizeObserver(() => this.updatePosition());
      if (this.header) this.resizeObserver.observe(this.header);
    }

    updatePosition() {
      if (this.details.open && this.header) {
        this.style.setProperty('--cs-menu-top', `${Math.max(0, this.header.getBoundingClientRect().bottom)}px`);
      }
    }

    syncOpen() {
      const open = this.details.open;
      this.summary.setAttribute('aria-expanded', String(open));
      document.body.classList.toggle('cs-category-menu-open', open);
      const sticky = this.closest('sticky-header');
      if (sticky) sticky.preventHide = open;
      if (open) {
        document.querySelectorAll('header-menu details[open]').forEach(details => { details.open = false; });
        this.updatePosition();
        this.querySelector('.cs-category-close').focus();
      } else {
        this.selectBranch(null, false);
      }
    }

    selectBranch(selected, open) {
      this.triggers.forEach(button => {
        const active = button === selected && open;
        button.setAttribute('aria-expanded', String(active));
        this.querySelector(`#${button.getAttribute('aria-controls')}`).hidden = !active;
      });
    }

    close(restoreFocus = true) {
      if (!this.details?.open) return;
      this.details.open = false;
      this.syncOpen();
      if (restoreFocus) this.summary.focus();
    }

    onKeyDown(event) {
      const activeTrigger = this.triggers.find(button => button.getAttribute('aria-expanded') === 'true');
      if (event.key === 'Escape' || (event.key === 'ArrowLeft' && event.target.closest('.cs-category-branch'))) {
        event.preventDefault();
        event.stopPropagation();
        if (activeTrigger) {
          this.selectBranch(null, false);
          activeTrigger.focus();
        } else this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.overlay.querySelectorAll('a[href], button:not([tabindex="-1"])')]
        .filter(element => element.getClientRects().length > 0);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      if (this.details?.open) {
        document.body.classList.remove('cs-category-menu-open');
        const sticky = this.closest('sticky-header');
        if (sticky) sticky.preventHide = false;
      }
    }
  });
}
